// Zentrale Toast-Nachrichten und Fehlermeldungen
// Alle UI-Texte an einem Ort für bessere Wartbarkeit

export const TOAST_MESSAGES = {
  // ============ Authentifizierung ============
  auth: {
    loginSuccess: {
      title: "Anmeldung erfolgreich",
      description: "Willkommen im internen Bereich.",
    },
    loginFailed: {
      title: "Anmeldung fehlgeschlagen",
      description: "E-Mail oder Passwort ist falsch.",
    },
    registerSuccess: {
      title: "Registrierung erfolgreich",
      description: "Sie können sich jetzt anmelden.",
    },
    userExists: {
      title: "Benutzer existiert bereits",
      description: "Diese E-Mail-Adresse ist bereits registriert. Bitte melden Sie sich an.",
    },
    logoutSuccess: {
      title: "Abgemeldet",
      description: "Sie wurden erfolgreich abgemeldet.",
    },
    genericError: {
      title: "Fehler",
      description: "Ein unerwarteter Fehler ist aufgetreten.",
    },
  },

  // ============ Datei-Upload & Analyse ============
  fileUpload: {
    analysisSuccess: (volumeCm3: number) => ({
      title: "Analyse erfolgreich",
      description: `Volumen: ${volumeCm3.toFixed(2)} cm³`,
    }),
    parseError: (message?: string) => ({
      title: "Fehler beim Parsen",
      description: message || "Die Datei konnte nicht verarbeitet werden",
    }),
    fileTooLarge: (sizeMB: string) => ({
      title: "Datei zu groß",
      description: `Die Datei ist ${sizeMB} MB groß. Maximal 50 MB erlaubt.`,
    }),
    invalidFormat: {
      title: "Ungültiges Dateiformat",
      description: "Bitte laden Sie eine STL-, OBJ- oder 3MF-Datei hoch",
    },
    stlAnalyzed: (fileName: string, weight: number, material: string, infill: number) => ({
      title: "STL Datei analysiert",
      description: `${fileName} - Effektives Gewicht: ${weight.toFixed(2)}g (${material}, ${infill}% Fülldichte)`,
    }),
    threeMfAnalyzed: (fileName: string, weight: number, material: string, infill: number) => ({
      title: "3MF Datei analysiert",
      description: `${fileName} - Effektives Gewicht: ${weight.toFixed(2)}g (${material}, ${infill}% Fülldichte)`,
    }),
    gcodeAnalyzed: (fileName: string, weight: number) => ({
      title: "GCODE Datei analysiert",
      description: `${fileName} - Materialgewicht aus GCODE: ${weight.toFixed(2)}g`,
    }),
    gcodeNoFilament: (fileName: string) => ({
      title: "GCODE hochgeladen",
      description: `${fileName} - Kein Filamentverbrauch gefunden. Bitte manuell eingeben.`,
    }),
    unsupportedFormat: {
      title: "Nicht unterstütztes Format",
      description: "Bitte lade eine STL-, 3MF- oder GCODE-Datei hoch.",
    },
  },

  // ============ Konfiguration ============
  config: {
    saveSuccess: {
      title: "Konfiguration gespeichert",
      description: "Die Einstellungen wurden in der Datenbank gespeichert.",
    },
    saveError: {
      title: "Fehler beim Speichern",
      description: "Die Einstellungen konnten nicht gespeichert werden.",
    },
    resetSuccess: {
      title: "Konfiguration zurückgesetzt",
      description: "Standardwerte wurden wiederhergestellt.",
    },
    resetError: {
      title: "Fehler beim Zurücksetzen",
      description: "Fehler beim Zurücksetzen.",
    },
    loadError: {
      title: "Fehler beim Laden",
      description: "Konfiguration konnte nicht geladen werden. Standardwerte werden verwendet.",
    },
  },

  // ============ PDF Layout ============
  pdfLayout: {
    saveSuccess: {
      title: "Gespeichert",
      description: "PDF-Layout-Konfiguration wurde erfolgreich gespeichert.",
    },
    saveError: {
      title: "Fehler beim Speichern",
      description: "PDF-Layout-Konfiguration konnte nicht gespeichert werden.",
    },
    resetSuccess: {
      title: "Zurückgesetzt",
      description: "PDF-Layout wurde auf Standardwerte zurückgesetzt.",
    },
    resetError: {
      title: "Fehler beim Zurücksetzen",
      description: "PDF-Layout konnte nicht zurückgesetzt werden.",
    },
    loadError: {
      title: "Fehler beim Laden",
      description: "PDF-Layout-Konfiguration konnte nicht geladen werden. Standardwerte werden verwendet.",
    },
    unexpectedError: {
      title: "Fehler",
      description: "Ein unerwarteter Fehler ist aufgetreten.",
    },
  },

  // ============ Backup ============
  backup: {
    loadError: {
      title: "Fehler beim Laden",
      description: "Backups konnten nicht geladen werden.",
    },
    nameRequired: {
      title: "Name erforderlich",
      description: "Bitte gib einen Namen für das Backup ein.",
    },
    createSuccess: (name: string) => ({
      title: "Backup erstellt",
      description: `"${name}" wurde erfolgreich gespeichert.`,
    }),
    createError: {
      title: "Fehler beim Erstellen",
      description: "Das Backup konnte nicht erstellt werden.",
    },
    restoreSuccess: (name: string) => ({
      title: "Backup wiederhergestellt",
      description: `"${name}" wurde erfolgreich geladen.`,
    }),
    restoreError: {
      title: "Fehler beim Wiederherstellen",
      description: "Das Backup konnte nicht wiederhergestellt werden.",
    },
    deleteSuccess: (name: string) => ({
      title: "Backup gelöscht",
      description: `"${name}" wurde entfernt.`,
    }),
    deleteError: {
      title: "Fehler beim Löschen",
      description: "Das Backup konnte nicht gelöscht werden.",
    },
    exportSuccess: {
      title: "Backup exportiert",
      description: "Die Datei wurde heruntergeladen.",
    },
    importSuccess: (date: string) => ({
      title: "Backup importiert",
      description: `Konfiguration vom ${date} wurde geladen. Klicke auf "Speichern" um die Änderungen zu übernehmen.`,
    }),
    importError: {
      title: "Import fehlgeschlagen",
      description: "Die Datei konnte nicht gelesen werden. Bitte überprüfe das Format.",
    },
  },

  // ============ Kontaktformular ============
  contact: {
    sendSuccess: {
      title: "Nachricht gesendet",
      description: "Vielen Dank! Wir melden uns schnellstmöglich bei Ihnen.",
    },
    validationError: {
      title: "Eingabefehler",
      description: "Bitte überprüfen Sie Ihre Eingaben.",
    },
    sendError: {
      title: "Fehler beim Senden",
      description: "Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt per E-Mail.",
    },
  },

  // ============ Instagram Admin ============
  instagram: {
    loadError: {
      title: "Fehler",
      description: "Instagram-Daten konnten nicht geladen werden.",
    },
    saveSuccess: {
      title: "Gespeichert",
      description: "Instagram-Einstellungen wurden aktualisiert.",
    },
    saveError: {
      title: "Fehler",
      description: "Einstellungen konnten nicht gespeichert werden.",
    },
    limitReached: {
      title: "Limit erreicht",
      description: "Maximal 10 Bilder/Videos erlaubt.",
    },
    videoTooLarge: {
      title: "Datei zu groß",
      description: "Videos dürfen maximal 50 MB groß sein.",
    },
    imageTooLarge: {
      title: "Datei zu groß",
      description: "Bilder dürfen maximal 10 MB groß sein.",
    },
    invalidFormat: {
      title: "Ungültiges Format",
      description: "Nur JPG, PNG, GIF, WEBP und MP4 werden unterstützt.",
    },
    uploadSuccess: {
      title: "Hochgeladen",
      description: "Datei wurde erfolgreich hochgeladen.",
    },
    uploadError: {
      title: "Fehler",
      description: "Datei konnte nicht hochgeladen werden.",
    },
    deleteSuccess: {
      title: "Gelöscht",
      description: "Eintrag wurde entfernt.",
    },
    deleteError: {
      title: "Fehler",
      description: "Eintrag konnte nicht gelöscht werden.",
    },
    updateSuccess: {
      title: "Aktualisiert",
      description: "Details wurden gespeichert.",
    },
    updateError: {
      title: "Fehler",
      description: "Details konnten nicht gespeichert werden.",
    },
    sortSuccess: {
      title: "Reihenfolge gespeichert",
      description: "Die neue Reihenfolge wurde übernommen.",
    },
    sortError: {
      title: "Fehler",
      description: "Reihenfolge konnte nicht gespeichert werden.",
    },
  },

  // ============ Galerie ============
  gallery: {
    deleteSuccess: {
      title: "Bild gelöscht",
      description: "Das Bild wurde erfolgreich entfernt.",
    },
    deleteError: {
      title: "Fehler beim Löschen",
      description: "Das Bild konnte nicht gelöscht werden.",
    },
    updateSuccess: {
      title: "Bild aktualisiert",
      description: "Die Änderungen wurden gespeichert.",
    },
    updateError: {
      title: "Fehler beim Aktualisieren",
      description: "Die Änderungen konnten nicht gespeichert werden.",
    },
    sortSuccess: {
      title: "Reihenfolge gespeichert",
      description: "Die neue Sortierung wurde übernommen.",
    },
    sortError: {
      title: "Fehler",
      description: "Reihenfolge konnte nicht gespeichert werden.",
    },
    uploadSuccess: (title: string) => ({
      title: "Bild hochgeladen",
      description: `"${title}" wurde erfolgreich hinzugefügt.`,
    }),
    uploadError: {
      title: "Fehler beim Hochladen",
      description: "Das Bild konnte nicht hochgeladen werden.",
    },
    fileTooLarge: {
      title: "Datei zu groß",
      description: "Bilder dürfen maximal 5 MB groß sein.",
    },
    invalidFormat: {
      title: "Ungültiges Format",
      description: "Nur JPG, PNG, GIF und WEBP werden unterstützt.",
    },
  },

  // ============ Angebot/Quote ============
  quote: {
    generateSuccess: {
      title: "Angebot erstellt",
      description: "Das PDF wurde erfolgreich generiert.",
    },
    generateError: {
      title: "Fehler",
      description: "Das Angebot konnte nicht erstellt werden.",
    },
  },
} as const;

// Typ-Export für autocomplete
export type ToastMessageKey = keyof typeof TOAST_MESSAGES;
